import React, { useEffect, useState } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import { object, string } from "yup";
import { TrainerFormValues } from "src/models/Trainer.model";
import TrainerForm from "../Layouts/TrainerForm";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import {
  useEditTrainerMutation,
  useViewTrainerByIdQuery,
} from "src/services/TrainerServices";
import { showToast } from "src/utils/showToaster";
import { useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { mobileRegExp } from "src/utils/validations/indianPhoneRegex";

const EditTrainerFormWrapper = () => {
  const { id = "" } = useParams();

  const [trainerData, setTrainerData] = useState<any>();

  const [editTriner] = useEditTrainerMutation();
  const { data, isLoading, isFetching } = useViewTrainerByIdQuery(id);

  useEffect(() => {
    if (!isFetching && !isLoading) {
      setTrainerData(data?.data);
    }
  }, [isFetching, isLoading, data]);
  // Form Initial Values
  const initialValues: TrainerFormValues = {
    trainer_name: trainerData?.name,
    mobile_number: trainerData?.mobile,
    whatsapp_number: trainerData?.whatsappMobileNo,
    emergency_contact_number: trainerData?.emergencyContactNo,
    dob: trainerData?.dob,
    email: trainerData?.email,
    marital: trainerData?.maritalStatus,
    branchId:
      trainerData?.branchId?.map((branch: any) => {
        return {
          label: branch?.branchName,
          value: branch?._id,
        };
      }) || [],
    gender: trainerData?.gender,
    address: trainerData?.address,
    blood_group: trainerData?.bloodGroup,
    academic_history: trainerData?.academicHistory?.map((el: any) => {
      return {
        collge_school_name: el?.collge_school_name,
        course_name: el?.course_name,
        course_type: el?.course_type,
        collge_school_tier: el?.collge_school_tier,
        start_year: el?.start_year,
        end_year: el?.end_year,
        currently_pursuing: el?.currently_pursuing,
      };
    }),
    work_history: trainerData?.workExperience?.map((el: any) => {
      return {
        company_name: el?.company_name,
        job_title: el?.job_title,
        job_description: el?.job_description,
        start_year: el?.start_year,
        end_year: el?.end_year,
        currently_pursuing: el?.currently_pursuing,
      };
    }),
  };

  // Validation Schema
  const validationSchema = object().shape({
    mobile_number: string()
    .trim()
    .matches(mobileRegExp, "mobile number is not valid"),
    whatsapp_number: string()
    .trim()
    .matches(mobileRegExp, "mobile number is not valid"),
    emergency_contact_number: string()
    .trim()
    .matches(mobileRegExp, "mobile number is not valid"),
    email: string().required("Please enter email").email("Invalid email"),

    trainer_name: string().required("Please enter name"),
    address: string().required("Please enter address"),

    dob: string().required("Please enter date of birth"),
  });

  // Handle Submit
  const handleSubmit = (
    values: TrainerFormValues,
    { setSubmitting, resetForm }: FormikHelpers<TrainerFormValues>
  ) => { 
      // Step 1: Remove the country code if present
   const withoutCountryCode = values?.mobile_number?.replace(/^\+91\s?/, "");

   // Step 2: Remove all spaces
   const finalNumber = withoutCountryCode?.replace(/\s/g, "");
    const formattedValues = {
      name: values?.trainer_name,
      email: values?.email,
      mobile: finalNumber,
      altMobile: values?.trainer_name,
      whatsappMobileNo: values?.whatsapp_number,
      emergencyContactNo: values?.emergency_contact_number,
      branchId: values?.branchId?.map((branch) => branch?.value),
      address: values?.address,
      gender: values?.gender,
      dob: values?.dob,
      maritalStatus: values?.marital,
      academicHistory: values?.academic_history?.map((el: any) => {
        return {
          collge_school_name: el?.collge_school_name,
          course_name: el?.course_name,
          course_type: el?.course_type,
          collge_school_tier: el?.collge_school_tier,
          start_year: el?.start_year,
          end_year: el?.end_year,
          currently_pursuing: el?.currently_pursuing,
        };
      }),
      workExperience: values?.work_history?.map((el: any) => {
        return {
          company_name: el?.company_name,
          job_title: el?.job_title,
          job_description: el?.job_description,
          start_year: el?.start_year,
          end_year: el?.end_year,
          currently_pursuing: el?.currently_pursuing,
        };
      }),
      bloodGroup: values?.blood_group,
    };
    editTriner({
      body: formattedValues,
      id: trainerData?._id,
    }).then((res: any) => {
      if (res?.error) {
        showToast("error", res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast("success", res?.data?.message);
          resetForm();
        } else {
          showToast("error", res?.data?.message);
        }
      }
      setSubmitting(false);
    });
  };

  return (
    <SideNavLayout>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            {(isLoading || isFetching) && (
              <div className="absolute w-[100%] h-[100%] flex justify-center items-center z-10 bg-slate-100 opacity-50">
                <CircularProgress />
              </div>
            )}
            <TrainerForm formikProps={formikProps} />
          </Form>
        )}
      </Formik>
    </SideNavLayout>
  );
};

export default EditTrainerFormWrapper;
